import { HIGHLIGHT_FILL_HANDLE_CLASS } from '@databases/highlights/constants';
import { type MouseInputAdapter } from '@databases/selections';

import {
  createDomMouseInputAdapter,
  getDatabaseScrollContainer,
  type HitTargetResolver,
  parseGridCoord,
  resolveCellCoordinates,
  resolveLayoutShell,
} from '../mouse-input-adapters/dom-mouse-input-adapter.ts';
import { isPointerOnDatabaseScrollableNativeScrollbar } from '../native-scrollbar-hit.ts';

const HEADER_CHECKBOX_SELECTOR = '#selections-header-checkbox';
const ROW_BUTTON_SELECTOR = '[data-testid="row-select-button"]';
const ROW_CHECKBOX_SELECTOR = '.selections-entry-checkbox';
const SELECTION_PRESERVING_SURFACE_SELECTOR = '[data-database-selection-preserve="true"]';
const FILL_HANDLE_SELECTOR = `.${HIGHLIGHT_FILL_HANDLE_CLASS}`;
const PINNED_LEADING_BOUNDARY_SELECTOR =
  '[data-dbs-pinned-leading-freeze-boundary="true"]';
const DATABASE_BOE_PROMPT_INPUT_SELECTOR =
  '[data-testid="database-blank-object-experience"] [contenteditable]:not([contenteditable="false"]), [data-testid="database-blank-object-experience"] .ProseMirror';

const resolveHeaderCheckbox: HitTargetResolver = (element) =>
  element.closest(HEADER_CHECKBOX_SELECTOR) ? { type: 'headerCheckbox' } : null;

const resolveRowButton: HitTargetResolver = (element) => {
  const rowSelectButton = element.closest(ROW_BUTTON_SELECTOR);

  if (!rowSelectButton) {
    return null;
  }

  const row = parseGridCoord(
    rowSelectButton.closest('tr[data-row-index]')?.getAttribute('data-row-index') ?? null,
  );

  return row ? { type: 'row', row, source: 'row-handle' } : { type: 'none' };
};

const resolveRowCheckbox: HitTargetResolver = (element) => {
  const rowCheckbox = element.closest(ROW_CHECKBOX_SELECTOR);

  if (!rowCheckbox) {
    return null;
  }

  const row = parseGridCoord(rowCheckbox.getAttribute('data-row-index'));

  return row ? { type: 'row', row, source: 'row-checkbox' } : { type: 'none' };
};

const resolveCell: HitTargetResolver = (element) => {
  const cell = element.closest('[data-row-index][data-col-index]');

  if (!cell) {
    return null;
  }

  const target = resolveCellCoordinates(cell);

  return target.type === 'cell'
    ? {
        ...target,
        horizontalAutoScroll:
          cell.getAttribute('data-dbs-pinned-leading') === 'true'
            ? 'disabled'
            : undefined,
      }
    : target;
};

const resolveFillHandle: HitTargetResolver = (element) =>
  element.closest(FILL_HANDLE_SELECTOR) ? { type: 'fillHandle' } : null;

const resolveOutside: HitTargetResolver = (element, clientX, clientY) => {
  const layoutShellTarget = resolveLayoutShell(element, clientX, clientY);

  if (!layoutShellTarget) {
    return null;
  }

  const isInteractive = element.closest(
    'table, button, [role="button"], input, textarea, a, [data-testid="database-value-view-container"], [data-testid="layout-right-side-panel"]',
  );
  const isSelectionPreservingSurface = element.closest(
    SELECTION_PRESERVING_SURFACE_SELECTOR,
  );
  const isDatabaseBoePromptInput = element.closest(DATABASE_BOE_PROMPT_INPUT_SELECTOR);

  if (isInteractive || isSelectionPreservingSurface || isDatabaseBoePromptInput) {
    return { type: 'none' };
  }

  return isPointerOnDatabaseScrollableNativeScrollbar(element, clientX, clientY)
    ? { type: 'none' }
    : { type: 'outside' };
};

const hitTargetResolvers: readonly HitTargetResolver[] = [
  resolveHeaderCheckbox,
  resolveRowButton,
  resolveRowCheckbox,
  resolveCell,
  resolveFillHandle,
  resolveOutside,
];

/**
 * Adapts the current Databases layout DOM to the layout-neutral mouse input contract.
 * Selectors and geometry remain here so another renderer can provide semantic hit targets
 * without changing `@databases/selections`.
 */
export function createTableMouseInputAdapter(): MouseInputAdapter {
  return createDomMouseInputAdapter(hitTargetResolvers, () => {
    const fixedBoundary = getDatabaseScrollContainer()?.querySelector(
      PINNED_LEADING_BOUNDARY_SELECTOR,
    );

    return fixedBoundary?.getBoundingClientRect().right ?? null;
  });
}
